import React, {useCallback, useEffect, useMemo, useState} from "react";
import {CircularProgress, DialogTitle, Divider} from "@mui/material";
import Dialog from "../../base/dialog";
import ModifyProductAvailabilityDialogSerializableSection from "./sections/serializable";
import ModifyProductAvailabilityDialogUnserializableSection from "./sections/un-serializable";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";


const ModifyProductAvailabilityDialog = ({
                                             open,
                                             close,
                                             partNo,
                                             currentAvailability,
                                             serialized,
                                             increase,
                                             setNewAvailability,
                                             warehouse,
                                             handleLocalUpdate,
                                             alreadySelectedSerials,
                                             alreadyAppliedCost,
                                         }) => {
    const [keyInformation, setKeyInformation] = useState({});
    const [loading, setLoading] = useState(true);
    const isMounted = useIsMounted();

    /**
     * Fetches the product's key information from the server.
     * @type {(function(): Promise<void>)|*}
     */
    const getProductKeyInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getProductKeyInformation(partNo, warehouse?.code);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setKeyInformation(response?.data ?? {});
        }
        setLoading(false);
    }, [isMounted, partNo, warehouse?.code])

    /**
     * With each change in the [open] state of the dialog:
     * - if dialog is open then fetches the product's key information from the server.
     */
    useEffect(() => {
        if (!open) {
            setKeyInformation({});
            setLoading(true);
            return;
        }
        getProductKeyInformation().then();
    }, [open, getProductKeyInformation]);

    const title = useMemo(() => {
        if (increase) {
            return `Increase ${partNo ?? ''} Availability`
        }
        return `Decrease ${partNo ?? ''} Availability`
    }, [increase, partNo])

    const content = useMemo(() => {
        if (loading) {
            return (
                <div className={'my-5 py-5 d-flex align-items-center justify-content-center'}>
                    <CircularProgress
                        size={30}
                        color={"primary"}
                    />
                </div>
            )
        }
        switch (serialized) {
            case true:
                return <ModifyProductAvailabilityDialogSerializableSection
                    increase={increase}
                    partNo={partNo}
                    currentAvailability={currentAvailability}
                    setNewAvailability={setNewAvailability}
                    close={close}
                    alreadySelectedSerials={alreadySelectedSerials}
                    handleLocalUpdate={handleLocalUpdate}
                    keyInformation={keyInformation}
                    alreadyAppliedCost={alreadyAppliedCost}
                />
            case false:
                return <ModifyProductAvailabilityDialogUnserializableSection
                    increase={increase}
                    partNo={partNo}
                    currentAvailability={currentAvailability}
                    setNewAvailability={setNewAvailability}
                    close={close}
                    keyInformation={keyInformation}
                />
            default:
                return null;
        }
    }, [loading, serialized, increase, partNo, currentAvailability, setNewAvailability, close, alreadySelectedSerials, handleLocalUpdate, keyInformation, alreadyAppliedCost]);

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={serialized ? 'md' : 'sm'}
                className={'bound-dialog modify-product-availability-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {title}
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                {content}
            </Dialog>
        </>
    )
}

export default ModifyProductAvailabilityDialog;
